import { ProtocolSaveBlockers } from '@/components/admin/protocoles/protocol-blockers';
import { ProtocolEditorFields } from '@/components/admin/protocoles/protocol-editor-fields';
import { ProtocolProblemsPill } from '@/components/admin/protocoles/protocol-problems';
import { ProtocolReadingWarning } from '@/components/admin/protocoles/reading/protocol-reading-warning';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { useAdminSession } from '@/lib/admin-session';
import { experiencesPlayingNoyauAt, sectionOrphanWarnings } from '@/lib/analyst-reading';
import { fetchCriteria, protocolsApi } from '@/lib/api';
import type { ProtocolDraft } from '@/lib/protocol-draft';
import { PROTOCOL_DRAFT_KIT, canSaveProtocol } from '@/lib/protocol-draft';
import { protocolRefusalSentence } from '@/lib/protocol-labels';
import type { ProtocolEditorView } from '@/lib/protocol-search';
import { viewSearch } from '@/lib/protocol-search';
import { PROTOCOL_WORDING } from '@/lib/protocol-wording';
import type {
  CriterionCard,
  CriterionState,
  ProtocolAnalyst,
  ProtocolContent,
  ProtocolDetail,
  ProtocolDraftContent,
  ProtocolStepDraft,
  ProtocolVersion,
  UsageLine,
  VersionSummary,
  VersionTag,
} from '@matter_extractor/shared';
import {
  analystSectionComplete,
  analystSectionGaps,
  criterionStateOfCard,
  productionBlockers,
  protocolProblems,
  rangementProblems,
} from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useState } from 'react';

/**
 * L'éditeur d'un protocole : le patron de §4, plus les surfaces de §7.
 *
 * Everything this screen does that the two editors before it do identically -
 * the head, the history, « utilisé dans », the retirement, the three boxes, the
 * debounced autosave and its optimistic control, the rename on blur - is
 * `VersionedEditor`. What is left here is the six things the layer asks of every
 * consumer (its API, its kit, its wording, its form, its keys, its links) plus
 * the four openings #49 added, each one a sentence of §7.1.
 *
 * ## The selection is UI state and lives here, above the two surfaces
 *
 * The frise and the step editor are both controlled and both need it - one
 * presses a card, the other renders the step's panels - so it can only live
 * above both. It is NOT in the URL, unlike `?version=`: a step somebody is
 * looking at is a posture of the reader, and a link that pinned it would carry a
 * step id into a thread where nobody can resolve it.
 *
 * It is also SEEDED from the draft and corrected against it on every render
 * rather than by an effect: a step removed by the step menu, by the frise's
 * trash, or by a « Repartir de cette version » that loaded a shorter protocol
 * would otherwise leave the selection pointing at nothing, and `StepEditor`
 * renders null on a step it cannot find - a blank half-page under a live frise.
 *
 * ## The criteria library is read HERE, and the editor does NOT wait for it
 *
 * The opposite of `persona-editor.tsx`, and for the reason that file gives: the
 * persona's KIT is built from the involvement scale, so mounting before the
 * scale lands would seed a draft on the wrong cran with no second chance. A
 * protocol's kit needs nothing external - the criteria feed the step's rows and
 * the production blockers, both of which are RENDERED and neither of which is
 * seeded. So the editor mounts immediately and the rows fill a beat later,
 * carrying `criteriaRead` so nothing accuses a criterion before the library is
 * known (the régime of `libraryRead` in the persona form).
 *
 * That flag is why `renderSaveBlockers` claims no blocker while the library is
 * unread: `productionBlockers` reports an id it cannot resolve as
 * `criterion-unknown`, so running it against an empty library would refuse the
 * tick on every criterion of the protocol - a red box that clears itself a
 * second later, which is worse than no box.
 *
 * ## The two cache keys, and the one it shares
 *
 * `['protocol', email, id]` holds a `ProtocolDetail`; `['protocols', email]`
 * holds the card list. Different prefixes on purpose: the trap of issue #16 is
 * two readers agreeing on a key and disagreeing on what is under it, and
 * `useQuery` is generic per component so nothing would catch the drift.
 * `['criteria', email]` is the key the criteria LIST already writes,
 * deliberately: same fetcher, same schema, same shape, so a criterion renamed in
 * its own library shows up here without a reload.
 *
 * ## The section Analyste, on both sides (issues #154 and #196)
 *
 * The switch of §3.5 is the form's (`protocol-editor-fields.tsx`); its position
 * is the URL's, so this file only relays `view` and `onSelectView` - and keeps
 * it across the birth of a protocol, whose first save navigates to its own
 * address. A line of the « N problèmes » pill names a STEP, so following one
 * brings the conduite back: a step selected under the section would be a click
 * that shows nothing.
 *
 * The boxes say the other half (§3.7, K16, P9, N37): a version whose section is
 * INCOMPLETE - a level no objective carries, or an empty reading - warns, never
 * refuses, naming the experiences it would stop from starting, those whose usage
 * line plays the noyau sequence at the tag the gesture moves (`playsNoyauAt`,
 * computed by the server). `development` for a save, plus `production` once the box
 * is ticked; the target's tag for a pointer box, whose content the layer reads for
 * either pointer.
 *
 * ## The orphan chips (issues #158 and #196, N52)
 *
 * The same yellow encart warns of an objective a reading or a direction cites and
 * the version no longer carries (`sectionOrphanWarnings`: the two readings of the
 * section by their pass, then `protocolOrphanWarnings` of `shared` for the
 * directions and the consigne, the walk `check_protocol` runs): on the DRAFT for the
 * save box, on the content of the version the pointer moves onto for « Passer en
 * production », « Reculer développement » and a rollback - whoever plays the
 * protocol, since the fact is the content's own. A warning, never a refusal:
 * `blocked` still reads the problems and the blockers alone.
 */

export interface ProtocolEditorProps {
  /** The entity, or null for a protocol that does not exist yet. */
  detail: ProtocolDetail | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: ProtocolVersion | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the entity from the server - what the stale-draft box offers. */
  onReload: () => void;
  /**
   * Select the name field on opening (§4.2): true on the editor a duplication
   * has just navigated to, which the route reads off `?copie`.
   */
  selectName?: boolean;
  /** The position of the switch of §4.9 the URL names - the conduite by default (N47). */
  view?: ProtocolEditorView;
  /** Where the switch goes: the route writes `?vue=`. */
  onSelectView?: (view: ProtocolEditorView) => void;
}

export function ProtocolEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
  selectName = false,
  view = 'conduite',
  onSelectView = () => undefined,
}: ProtocolEditorProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  const library = useQuery({
    queryKey: ['criteria', email],
    queryFn: fetchCriteria,
    retry: false,
  });

  const criteria: readonly CriterionCard[] = library.data?.criteria ?? [];
  const criteriaRead = library.data !== undefined;

  /**
   * What the two surfaces are shown as selected: the state when it still names
   * a step of the draft, the first step otherwise.
   *
   * Derived on every render rather than repaired by an effect, which is the
   * repo's rule and also the honest shape: the draft is the authority on which
   * steps exist, and this is a projection of the state onto it.
   */
  /**
   * What the two production checks know about one cited criterion (§4).
   *
   * ONE lookup for the save box and for the pointer box, because they ask the
   * same question of the same library - and because `productionBlockers`
   * answers `criterion-unknown` for an id it cannot resolve, so a second
   * spelling that forgot the `criteriaRead` guard would accuse every criterion
   * of a protocol for as long as the library took to arrive.
   */
  const states = useMemo(
    () =>
      // Per VERSION (§7.2): whether a threshold may be posed on a citation
      // depends on the version THAT citation points at - `latest` is the
      // development one, and the card carries the notation of the two pointed
      // versions (`criterionStateOfCard`).
      new Map<string, CriterionState>(
        criteria.map((card) => [card.id, criterionStateOfCard(card)]),
      ),
    [criteria],
  );
  // Indexed ONCE per library rather than scanned per citation, and stable
  // across renders: the two boxes run `productionBlockers` on every keystroke
  // of the autosave, and a protocol citing a dozen criteria out of a library of
  // a hundred would walk the hundred a dozen times each time.
  const lookup = useCallback(
    (criterionId: string): CriterionState | undefined => states.get(criterionId),
    [states],
  );

  function selectionIn(draft: ProtocolDraft): string {
    const first = draft.steps[0]?.id ?? '';
    if (selected === null) return first;
    return draft.steps.some((step) => step.id === selected) ? selected : first;
  }

  /** Who plays this protocol - the experiences a version without a reading would stop (N37). */
  const usage: readonly UsageLine[] = detail?.usage ?? [];

  /**
   * The yellow encart of §3.7 for a version of these steps and this section, moved
   * onto these tags - or null when it has nothing to say: the section is COMPLETE
   * (spaces alone do not count as a reading, N35) or no noyau experience plays the
   * protocol at those tags, AND no objective a reading or a direction cites is gone
   * (#158).
   */
  function sectionWarning(
    steps: readonly ProtocolStepDraft[],
    analyst: ProtocolAnalyst,
    consigne: string,
    tags: readonly VersionTag[],
    gesture: 'save' | 'pointer',
  ): ReactNode {
    const gaps = analystSectionGaps(steps, analyst);
    const incomplete = !analystSectionComplete(gaps);
    const stopped = incomplete ? experiencesPlayingNoyauAt(usage, tags) : [];
    const orphans = sectionOrphanWarnings(steps, analyst, consigne);
    return stopped.length === 0 && orphans.length === 0 ? null : (
      <ProtocolReadingWarning
        experiences={stopped}
        gaps={incomplete ? gaps : null}
        orphans={orphans}
        gesture={gesture}
      />
    );
  }

  return (
    <VersionedEditor<ProtocolContent, ProtocolDraftContent, ProtocolDraft>
      wording={PROTOCOL_WORDING}
      api={protocolsApi}
      kit={PROTOCOL_DRAFT_KIT}
      keys={{ detail: 'protocol', list: 'protocols' }}
      hasReferents
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      // §7.1: the right column is taken by the step's rules, so the three panels
      // of §4 move into a drawer. « La seule divergence avec les éditeurs de
      // critère et de persona. »
      layout="drawer"
      onCreated={(protocolId) =>
        void navigate({
          to: '/admin/protocoles/$protocolId',
          params: { protocolId },
          // The view survives the birth: a first version saved from the
          // reading reopens on the reading.
          search: viewSearch(view),
        })
      }
      selectName={selectName}
      renderHeaderExtra={(draft) => (
        <ProtocolProblemsPill
          problems={[...protocolProblems(draft.steps), ...rangementProblems(draft)]}
          onGoTo={(stepId) => {
            setSelected(stepId);
            // A problem names a STEP: under the reading, selecting it would show nothing.
            if (view !== 'conduite') onSelectView('conduite');
          }}
        />
      )}
      renderSaveBlockers={(draft, alsoProduction) => {
        const problems = [...protocolProblems(draft.steps), ...rangementProblems(draft)];
        // No blocker is CLAIMED before the library has been read - see `lookup`.
        const blockers =
          alsoProduction && criteriaRead ? productionBlockers(draft.steps, lookup) : [];
        // The tick moves production too, so it names who plays production as well.
        const warning = sectionWarning(
          draft.steps,
          draft.analyst,
          draft.rangement.consigne,
          alsoProduction ? ['development', 'production'] : ['development'],
          'save',
        );
        if (problems.length === 0 && blockers.length === 0 && warning === null) return null;
        return {
          content: (
            <div className="space-y-3">
              <ProtocolSaveBlockers problems={problems} blockers={blockers} />
              {warning}
            </div>
          ),
          // The section WARNS and never refuses (P9, K16, N37), nor do its orphans
          // (V2, N52): the button stays armed.
          blocked: problems.length > 0 || blockers.length > 0,
        };
      }}
      /*
        The SAME rule, on the version the history is aiming production at (§7.1).
        It is the version's own content and not the draft: « Passer en
        production » from the history moves the pointer onto an old version,
        whose references are not the ones being typed. The layer reads it and
        hands it here - for either pointer since #154, the section being as
        incomplete on a version « Reculer développement » goes back to.
      */
      renderPointerBlockers={(content, tag) => {
        const blockers =
          tag === 'production' && criteriaRead ? productionBlockers(content.steps, lookup) : [];
        const warning = sectionWarning(
          content.steps,
          content.analyst,
          content.rangement.consigne,
          [tag],
          'pointer',
        );
        if (blockers.length === 0 && warning === null) return null;
        return {
          content: (
            <div className="space-y-3">
              <ProtocolSaveBlockers problems={[]} blockers={blockers} />
              {warning}
            </div>
          ),
          blocked: blockers.length > 0,
        };
      }}
      // The two 409s of §7.1, which are not among the six codes of §4.
      ownRefusal={protocolRefusalSentence}
      // The three refusals of §4 plus the one H17 adds to a NEW protocol: the
      // turn cap of every step. Pure, and asserted in `protocol-draft.test.ts`.
      canSave={canSaveProtocol}
      renderFields={(binding) => (
        <ProtocolEditorFields
          binding={binding}
          selectedStepId={selectionIn(binding.draft)}
          onSelect={setSelected}
          criteria={criteria}
          criteriaRead={criteriaRead}
          view={view}
          onSelectView={onSelectView}
        />
      )}
    />
  );
}
